import type { StockDetailData, StockHolderNumberPoint, StockHolderTradeRow, StockKlineBar, StockNotesTabProps } from "fin-web-components";
import { useCallback, useMemo, useState } from "react";
import type { StockNoteItem } from "../../products/user_holdings/UserStockNoteType";
import { globalModel } from "../../global/GlobalModel";
import { useUserNotesEntitlement } from "./UserNotesEntitlementContext";
import { useMyNotesNavigate } from "./useMyNotesNavigate";
import { useStockNotesTab } from "./useStockNotesTab";
import StockKlineApi from "./StockKlineApi";
import StockHolderApi from "./StockHolderApi";
import { queryStockDetailInfoForLongfans } from "./stockDetailQuery";
import { isOverseasStockCode } from "./stockMarketUtils";

export type StockLinkDetailState = {
  code: string;
  data: StockDetailData | null;
  dayKlines: StockKlineBar[] | null;
  holderNumbers: StockHolderNumberPoint[] | null;
  holderTrades: StockHolderTradeRow[] | null;
  loading: boolean;
  klineLoading: boolean;
  holderTradesLoading: boolean;
  notesOnly: boolean;
};

/**
 * 与 fin-web-components `StockLink` 配合：页面级缓存当前打开的标的详情、日线与笔记。
 * 笔记 Tab 依赖本空间「我的笔记」产品权益。
 */
export function useStockLinkDetail(spaceId: string): {
  stockLinkDetail: StockLinkDetailState;
  requestStockDetailForLink: (stockCode: string) => void;
  buildStockNotesTabProps: (stockCode: string, stockName?: string) => StockNotesTabProps | null;
  loadPanel: (stockCode: string) => Promise<void>;
  listNotesForImport: (stockCode: string) => StockNoteItem[];
  notesEntitled: boolean;
  isOverseasStock: (stockCode: string) => boolean;
} {
  const { notesEntitled } = useUserNotesEntitlement();
  const { loadPanel, buildStockNotesTabProps, listNotesForImport } = useStockNotesTab(
    spaceId,
    notesEntitled
  );
  const goMyNotes = useMyNotesNavigate(spaceId);

  const buildStockNotesTabPropsWithNav = useMemo(
    () =>
      (stockCode: string, stockName?: string): StockNotesTabProps | null => {
        const base = buildStockNotesTabProps(stockCode, stockName);
        if (!base) {
          return null;
        }
        return {
          ...base,
          onViewAllNotes: () => goMyNotes(stockCode),
        };
      },
    [buildStockNotesTabProps, goMyNotes]
  );

  const [stockLinkDetail, setStockLinkDetail] = useState<StockLinkDetailState>({
    code: "",
    data: null,
    dayKlines: null,
    holderNumbers: null,
    holderTrades: null,
    loading: false,
    klineLoading: false,
    holderTradesLoading: false,
    notesOnly: false,
  });

  const isOverseasStock = useCallback(
    (stockCode: string) => isOverseasStockCode(stockCode, globalModel.overseasStocksCatalog),
    []
  );

  const requestStockDetailForLink = useCallback(
    (code: string) => {
      const c = code.trim();
      if (!c) {
        return;
      }

      setStockLinkDetail((prev) => {
        if (prev.code === c && prev.data != null && !prev.loading) {
          return prev;
        }
        if (prev.code === c && prev.loading) {
          return prev;
        }
        return {
          code: c,
          data: null,
          dayKlines: null,
          holderNumbers: null,
          holderTrades: null,
          loading: true,
          klineLoading: true,
          holderTradesLoading: true,
          notesOnly: false,
        };
      });

      if (notesEntitled) {
        void loadPanel(c);
      }

      void (async () => {
        const overseas = isOverseasStockCode(c, globalModel.overseasStocksCatalog);

        if (overseas) {
          const hit = globalModel.overseasStocksCatalog.find(
            (s) => s.code.trim().toUpperCase() === c.toUpperCase()
          );
          const minimal: StockDetailData = {
            stockCode: c,
            stockName: hit?.name ?? c,
            latestPrice: 0,
            priceChange: 0,
            positionScore: 0,
            performanceScore: 0,
            stockDescription: "",
            finances: [],
            stockConcepts: [],
            majorEvents: [],
            mainHoldings: [],
          };
          setStockLinkDetail((prev) =>
            prev.code === c
              ? {
                  code: c,
                  data: minimal,
                  dayKlines: null,
                  holderNumbers: null,
                  holderTrades: null,
                  loading: false,
                  klineLoading: false,
                  holderTradesLoading: false,
                  notesOnly: true,
                }
              : prev
          );
          return;
        }

        const [data, dayKlines, holderNumbers, holderTrades] = await Promise.all([
          queryStockDetailInfoForLongfans(c),
          StockKlineApi.queryStockDayKlines(c),
          StockHolderApi.queryStockHolderNumbers(c),
          StockHolderApi.queryStockHolderTrades(c),
        ]);
        setStockLinkDetail((prev) =>
          prev.code === c
            ? {
                code: c,
                data,
                dayKlines,
                holderNumbers,
                holderTrades,
                loading: false,
                klineLoading: false,
                holderTradesLoading: false,
                notesOnly: false,
              }
            : prev
        );
      })();
    },
    [loadPanel, notesEntitled]
  );

  return {
    stockLinkDetail,
    requestStockDetailForLink,
    buildStockNotesTabProps: buildStockNotesTabPropsWithNav,
    loadPanel,
    listNotesForImport,
    notesEntitled,
    isOverseasStock,
  };
}

/** 与 StockLink 对齐的详情/K线/股东数据 props */
export function stockLinkDetailPropsForCode(
  stockLinkDetail: StockLinkDetailState,
  stockCode: string
): {
  dayKlines: StockKlineBar[] | null;
  klineLoading: boolean;
  holderNumbers: StockHolderNumberPoint[] | null;
  holderTrades: StockHolderTradeRow[] | null;
  holderTradesLoading: boolean;
} {
  const matched = stockLinkDetail.code === stockCode.trim();
  return {
    dayKlines: matched ? stockLinkDetail.dayKlines : null,
    klineLoading: matched ? stockLinkDetail.klineLoading : false,
    holderNumbers: matched ? stockLinkDetail.holderNumbers : null,
    holderTrades: matched ? stockLinkDetail.holderTrades : null,
    holderTradesLoading: matched ? stockLinkDetail.holderTradesLoading : false,
  };
}
